import { useState } from 'react'
import { Check, Clipboard, Download, Eye, EyeOff, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { showToastMessage } from '@/utils'

export interface OneTimeAccountDetail {
  name: string
  email: string
  mobile: string
  roles: string[]
}

export interface OneTimePasswordRevealProps {
  password: string
  notice?: string
  account: OneTimeAccountDetail
  onDismiss: () => void
}

const csvField = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`

export const buildCredentialsCsv = (
  account: OneTimeAccountDetail,
  password: string,
  loginUrl: string,
): Blob => {
  const header = ['User name', 'Email', 'Mobile', 'Roles', 'Password', 'Login URL']
  const row = [account.name, account.email, account.mobile, account.roles.join('|'), password, loginUrl]
  const csv = [header, row].map((line) => line.map(csvField).join(',')).join('\r\n') + '\r\n'
  return new Blob([csv], { type: 'text/csv;charset=utf-8' })
}

const downloadCredentialsCsv = (account: OneTimeAccountDetail, password: string) => {
  const blob = buildCredentialsCsv(account, password, `${window.location.origin}/login`)
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `credentials_${account.email.replace(/[^a-z0-9]+/gi, '_')}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export function OneTimePasswordReveal({ password, notice, account, onDismiss }: OneTimePasswordRevealProps) {
  const [open, setOpen] = useState(true)
  const [revealed, setRevealed] = useState(false)
  const [captured, setCaptured] = useState(false)

  const close = () => {
    setOpen(false)
    onDismiss()
  }

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCaptured(true)
      showToastMessage('Password copied', true)
    } catch {
      showToastMessage('Unable to copy password', false)
    }
  }

  const download = () => {
    downloadCredentialsCsv(account, password)
    setCaptured(true)
    showToastMessage('Credentials CSV downloaded', true)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) setOpen(true)
        else if (captured) close()
      }}
    >
      <DialogContent
        className="max-w-lg"
        onEscapeKeyDown={(event) => { if (!captured) event.preventDefault() }}
        onPointerDownOutside={(event) => { event.preventDefault() }}
        onInteractOutside={(event) => { event.preventDefault() }}
      >
        <DialogHeader>
          <DialogTitle>Save the generated password</DialogTitle>
          <p className="text-text-muted">
            This password is shown once and cannot be retrieved later.
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div role="alert" className="flex items-start gap-3 rounded-md border border-border border-l-4 border-l-warning-icon bg-warning-subtle p-3 text-text">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
            <p>{notice || 'Share this password securely with the new user. Ask them to reset it after signing in.'}</p>
          </div>

          <div className="rounded-lg border border-border bg-surface-sunken p-4">
            <p className="font-medium text-text-secondary">{account.name || account.email}</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded-md border border-border-strong bg-surface px-3 py-2 font-mono text-base font-medium text-text">
                {revealed ? password : '••••••••••••••••'}
              </code>
              <Button type="button" size="icon" variant="secondary" onClick={() => setRevealed((value) => !value)} aria-label={revealed ? 'Hide password' : 'Reveal password'} title={revealed ? 'Hide password' : 'Reveal password'} aria-pressed={revealed}>
                {revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              </Button>
              <Button type="button" size="icon" variant="secondary" onClick={() => void copyPassword()} aria-label="Copy password" title="Copy password">
                <Clipboard aria-hidden="true" />
              </Button>
            </div>
          </div>

          <p className="text-text-muted">Login: {account.email} · Mobile: {account.mobile}</p>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={download} leadingIcon={<Download aria-hidden="true" />}>
            Download .csv
          </Button>
          <Button type="button" onClick={close} disabled={!captured}>
            {captured ? <Check aria-hidden="true" /> : null}
            I have saved this password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
